import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Tooltip,
  Typography,
  Paper,
  Stack,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import React, { useEffect, useRef, useState } from "react";
import { getWeekdayName } from "../../utils/datetimeUtils";
import useFetch from "../../lib/useFetch";

const API = process.env.REACT_APP_API;

interface PaymentStepProps {
  seed: any;
  orderId?: number;
  total: number | null;
  isHomeDelivery: boolean;
  slug?: string;
  isLoadingSeed?: boolean;
  isLoadingUP?: boolean;
}

const PaymentStep: React.FC<PaymentStepProps> = ({
  seed,
  orderId,
  total,
  isHomeDelivery,
  slug = "",
  isLoadingSeed = false,
  isLoadingUP = false,
}) => {
  const apiFetch = useFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;

  const [qrCode, setQrCode] = useState("");
  const [pixPayload, setPixPayload] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const totalAmount =
    total !== null && total !== undefined ? total.toFixed(2) : "0";

  useEffect(() => {
    let isCancelled = false;

    const loadPixPayload = async () => {
      if (!seed || !seed.slug || !orderId || !total || total <= 0) {
        setQrCode("");
        setPixPayload("");
        return;
      }

      try {
        const data = await apiFetchRef.current(
          `${API}/api/feira_online/seeds/${seed.slug}/orders/${orderId}/pix`,
        );
        if (!data?.br_code || !data?.qr_code) {
          throw new Error("Erro ao carregar código PIX.");
        }

        if (!isCancelled) {
          setQrCode(data.qr_code);
          setPixPayload(data.br_code);
        }
      } catch {
        if (!isCancelled) {
          setQrCode("");
          setPixPayload("");
        }
      }
    };

    loadPixPayload();

    return () => {
      isCancelled = true;
    };
  }, [seed, orderId, total]);

  if (isLoadingUP || isLoadingSeed || !seed) {
    return <CircularProgress />;
  }

  if (total === null || total === 0) {
    return (
      <Box maxWidth={900} mx="auto" p={4}>
        <Typography variant="h4" component="h2" gutterBottom>
          Pagamento
        </Typography>
        <Typography variant="body1" gutterBottom>
          Valor total nulo ou não identificado, tente novamente.
        </Typography>
        <Box textAlign="center" mt={2}>
          <Button href={`/${slug}`} variant="contained" color="success">
            Voltar para a loja
          </Button>
        </Box>
      </Box>
    );
  }

  let pixKey = seed.seeder.pix_key;
  if (/\+55\d{10,11}/.test(pixKey)) {
    pixKey = pixKey.replace("+55", "");
  }

  const deliveryWeekday = getWeekdayName(seed.delivery_weekday);

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handlePIXCopyAndPasteClick = () => {
    navigator.clipboard.writeText(pixPayload);
    setTooltipOpen(true);
  };

  return (
    <Box maxWidth={900} mx="auto" p={4}>
      <Typography variant="h4" component="h2" gutterBottom align="center">
        Pagamento
      </Typography>
      <Typography variant="body1" gutterBottom align="center">
        Escaneie o QR code, copie o código PIX abaixo ou use a chave PIX da sua
        semeadora para pagar o pedido:
      </Typography>
      <Stack alignItems="center" spacing={2} mt={2}>
        {qrCode && (
          <img
            alt="qr code"
            src={qrCode}
            style={{ maxWidth: 200, width: "100%" }}
          />
        )}
        {pixPayload && (
          <>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                mb: 1,
                p: 2,
                textAlign: "center",
                wordBreak: "break-all",
                fontFamily: "monospace",
                fontSize: "1rem",
              }}
            >
              {pixPayload}
            </Paper>
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <div>
                <Tooltip
                  PopperProps={{ disablePortal: true }}
                  onClose={handleTooltipClose}
                  open={tooltipOpen}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  title="Copiado!"
                >
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={handlePIXCopyAndPasteClick}
                    color="primary"
                  >
                    Copiar código PIX
                  </Button>
                </Tooltip>
              </div>
            </ClickAwayListener>
          </>
        )}
      </Stack>
      <Paper
        variant="outlined"
        sx={{ p: 2, mt: 2, mb: 2, background: "#fafafa" }}
      >
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Valor do seu pedido: <b>R$ {totalAmount}</b>
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Chave PIX: <b>{pixKey}</b>
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Nome: <b>{seed.seeder.name}</b>
        </Typography>
        <Typography variant="body2">
          Banco: <b>{seed.seeder.bank}</b>
        </Typography>
      </Paper>
      <Typography variant="body1" align="center">
        Pronto!
      </Typography>
      {isHomeDelivery ? (
        <Typography variant="body1" align="center">
          A entrega está programada para: <b>{deliveryWeekday}</b>.
        </Typography>
      ) : (
        <Typography variant="body1" align="center">
          O dia para retirada da sua feira é: <b>{deliveryWeekday}</b>.
        </Typography>
      )}
      <Typography variant="body1" align="center" mb={2}>
        Agora sua semeadora cuidará da sua feira pra você.
      </Typography>
      <Box textAlign="center">
        <Button href={`/${slug}`} variant="contained" color="success">
          Voltar para a loja
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentStep;
